import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { storyblokEditable } from '@storyblok/react';
import { memo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import Link from '../../Links/Link/Link';
import { storyblokLinkToRoute } from '../util/storyblokLinkToRoute';
import { withBaseComponent } from '../hoc/withBaseComponent';

/**
 * StoryblokButton component renders a customizable button based on Storyblok data.
 * It handles different link types such as email, phone, and regular URLs.
 *
 * @param {object} props - Component props
 * @param {object} props.blok - Storyblok data object
 * @param {React.CSSProperties} [props.baseStyles] - Additional base styles for the button
 * @returns {JSX.Element} Rendered button component
 */
const StoryblokButtonComponent = ({ blok, baseStyles }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    general = {
      label: '',
      href: '',
      variant: 'contained',
      color: 'primary',
    },
    extra = { id: '' },
  } = blok;
  const { id } = extra;
  const { label, href, variant, color, wrap } = general;

  const rawUrl = href.url || href.cached_url || '';

  const linkTo = (() => {
    if (href.linktype === 'email') {
      return `mailto:${href.email}`;
    } else if (href.linktype === 'phone') {
      return `tel:${href.phone}`;
    } else if (href.linktype === 'story') {
      return storyblokLinkToRoute(rawUrl, intl.locale);
    } else {
      return rawUrl;
    }
  })();

  const linkProps =
    href.linktype === 'email' || href.linktype === 'phone'
      ? { href: linkTo }
      : {
        component: Link,
        to: linkTo,
      };

  const hasAnchorTag = typeof linkTo === 'string' && linkTo.includes('#');

  const handleAnchorClick = useCallback(
    (event) => {
      event.preventDefault();

      const anchor = linkTo.split('#')[1];
      const element = document.getElementById(anchor);

      const pathName = window.location.pathname;
      const anchorId = linkTo.split('#')[1];
      const routePath = `${pathName}#${anchorId}`;

      if (element) {
        navigate(routePath, { replace: true });
        element.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [navigate],
  );

  return (
    <Box
      id={id}
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box {...storyblokEditable(blok)}>
        <Button
          color={color}
          onClick={hasAnchorTag ? handleAnchorClick : undefined}
          sx={{
            ...baseStyles,
            whiteSpace: wrap || 'nowrap',
          }}
          target={href.target}
          variant={variant}
          {...linkProps}
        >
          {label}
        </Button>
      </Box>
    </Box>
  );
};

StoryblokButtonComponent.displayName = 'StoryblokButton';
const StoryblokButton = withBaseComponent(StoryblokButtonComponent);

export default memo(StoryblokButton);
